<!--
  O histórico do razão de combos, com link para a OS de cada movimento. Usado na
  aba Combos do cliente e no relatório por unidade.
-->
<template>
  <data-table
    :test-id-prefix="testIdPrefix"
    :config="config"
    :data="linhas"
    :endpoint="null"
    @button-click="handleButtonClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useMoedaFranquia } from '../../composables/useMoedaFranquia';
import type { MovimentoCombo } from '../../composables/useClienteCombos';
import { comboMovimentoLabel, comboExplanations } from './explanations';

const props = withDefaults(defineProps<{
  testIdPrefix: string;
  movimentos: MovimentoCombo[];
  franchiseId: number | string | null;
  permiteEstorno?: boolean;
  mostraCliente?: boolean;
}>(), { permiteEstorno: false, mostraCliente: false });

const emit = defineEmits<{
  estornar: [movimento: MovimentoCombo];
}>();

const { t } = useI18n();
const { formatDateTime } = useDateFormatter();
const { formatarMoeda } = useMoedaFranquia();

const EX = computed(() => comboExplanations(t));

// Consumos que já têm estorno apontando para eles: o backend recusaria o segundo.
const estornados = computed(() => new Set(
  props.movimentos.filter((m) => m.tipo === 'estorno').map((m) => m.origem_movimento_id),
));

const linhas = computed(() => props.movimentos.map((m) => ({
  ...m,
  tipo_nome: comboMovimentoLabel(t, m.tipo),
  data_hora: m.created_at ? formatDateTime(m.created_at) : '-',
  os_rotulo: m.order_id ? `#${m.order_id}` : '-',
})));

const comoMovimento = (row: Record<string, unknown>): MovimentoCombo => row as unknown as MovimentoCombo;

const config = computed(() => ({
  tableId: props.testIdPrefix,
  columns: [
    { data: 'data_hora', title: t('Data'), width: 160, sortable: false },
    ...(props.mostraCliente ? [{ data: 'cliente_nome', title: t('Cliente'), sortable: true }] : []),
    ...(props.mostraCliente ? [{ data: 'combo_nome', title: t('Combo'), sortable: true }] : []),
    { data: 'tipo_nome', title: t('Movimento'), width: 150, sortable: true },
    { data: 'quantidade', title: t('Pares'), width: 80, sortable: true },
    {
      data: 'os_rotulo',
      sortField: 'order_id',
      title: t('OS'),
      width: 90,
      sortable: true,
      renderType: 'custom_clickable_id' as const,
      clickCallback: (_value: unknown, row: Record<string, unknown>) => {
        const orderId = comoMovimento(row).order_id;
        if (orderId) eventBus.emit('modal.pdv-checkout.open', { orderId });
      },
    },
    {
      data: 'preco_matriz',
      title: t('Preço da matriz'),
      width: 130,
      sortable: true,
      calculated: true,
      explanation: EX.value.precoMatriz,
      cellRenderer: ({ data: row }: { data: Record<string, unknown> }): string => {
        const preco = comoMovimento(row).preco_matriz;
        return preco == null ? '-' : formatarMoeda(preco, props.franchiseId);
      },
    },
    { data: 'motivo', title: t('Motivo'), sortable: false },
    ...(props.permiteEstorno ? [{
      data: null,
      title: t('Ação'),
      width: 120,
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Estornar'),
      buttonIcon: 'pi pi-undo',
      buttonAction: 'estornar',
      buttonTooltip: () => EX.value.estornar.what,
      buttonCondition: (row: Record<string, unknown>) => {
        const m = comoMovimento(row);
        return m.tipo === 'consumo' && !estornados.value.has(m.movimento_id);
      },
      buttonTestId: (row: Record<string, unknown>) => `${props.testIdPrefix}-estornar-${comoMovimento(row).movimento_id}`,
    }] : []),
  ],
  pagination: false,
  sortable: true,
  showFilters: false,
  heightMode: 'auto',
  stripedRows: true,
  showGridlines: true,
  size: 'small',
}));

const handleButtonClick = (evento: { data: Record<string, unknown> | null; action?: unknown }): void => {
  if (evento.action === 'estornar' && evento.data) {
    emit('estornar', comoMovimento(evento.data));
  }
};
</script>
